import { DesignComponentGroup, } from "../../model/groups/SDKDesignComponentGroup";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Function Definition
export class DesignComponentGroupResolver {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(designComponents) {
        this.designComponents = designComponents;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Resolution
    resolveGroupData(data) {
        // 1. Create maps for efficient group lookup and resolution
        // hashedGroups stores the raw remote models for reference
        // resolvedGroups stores the processed DesignComponentGroup instances
        const hashedGroups = new Map();
        const resolvedGroups = new Map();
        // 2. Create maps for component-to-source mapping
        // These maps will be used later to assign source data to groups
        const designComponentIdsToSourceId = new Map();
        const designComponentIdsToSourceName = new Map();
        // 3. Build component-to-source mappings
        // This is the foundation for source propagation - we need to know which components
        // belong to which sources before we can assign sources to groups
        this.designComponents.forEach((designComponent) => {
            var _a, _b;
            if ((_a = designComponent.origin) === null || _a === void 0 ? void 0 : _a.sourceId) {
                designComponentIdsToSourceId.set(designComponent.id, designComponent.origin.sourceId);
            }
            if ((_b = designComponent.origin) === null || _b === void 0 ? void 0 : _b.fileName) {
                designComponentIdsToSourceName.set(designComponent.id, designComponent.origin.fileName);
            }
        });
        // 4. Convert raw groups to resolved groups
        // At this stage, we're just creating the basic group structure without
        // resolving references or computing relationships
        for (const rawGroup of data) {
            const group = new DesignComponentGroup(rawGroup);
            hashedGroups.set(rawGroup.persistentId, rawGroup);
            resolvedGroups.set(rawGroup.persistentId, group);
        }
        // 5. Build the reference tree and component lists
        // This step establishes parent-child relationships and identifies which
        // components belong to which groups
        for (const rawGroup of data) {
            const filteredDesignComponentIds = new Array();
            const referencedGroup = resolvedGroups.get(rawGroup.persistentId);
            // Skip if we couldn't resolve this group
            if (!referencedGroup)
                continue;
            // Process each child reference
            for (const id of rawGroup.childrenIds) {
                // Check if the reference is to another group or a component
                const childGroup = resolvedGroups.get(id);
                if (childGroup) {
                    // If it's a group, establish the parent-child relationship
                    referencedGroup.addChild(childGroup.persistentId);
                }
                else {
                    // If it's a component, add it to this group's component list
                    filteredDesignComponentIds.push(id);
                }
            }
            // Assign the filtered component IDs to the group
            referencedGroup.componentIds = filteredDesignComponentIds;
        }
        // 6. Retrieve all resolved groups for further processing
        const groups = Array.from(resolvedGroups.values());
        // 7. Compute group paths and parent relationships
        // These are needed for proper source propagation and group organization
        this.recomputePaths(groups);
        this.recomputeParents(groups);
        // 8. Assign source data to groups
        // This propagates source information from components up through the group hierarchy
        this.assignSourceDataToGroups(groups, designComponentIdsToSourceId, designComponentIdsToSourceName);
        // 9. Return groups ordered by their root relationships
        // This ensures a consistent and logical ordering of groups in the final output
        return this.reorderGroupsByRoots(groups);
    }
    assignSourceDataToGroups(groups, componentIdsToSourceId, componentIdsToSourceName) {
        // 1. Create a map of all groups for quick lookup by ID
        // This allows O(1) access to any group by its ID, which is crucial for efficient parent traversal
        const groupsById = new Map(groups.map((g) => [g.persistentId, g]));
        // 2. Create a map to collect all sources for each group (including from children)
        // Using Set to automatically handle duplicates during collection
        // This is more efficient than checking for duplicates manually during collection
        const groupSources = new Map();
        // 3. Helper to get sources for a group, creating a new set if needed
        const getOrCreateGroupSources = (groupId) => {
            let sources = groupSources.get(groupId);
            if (!sources) {
                sources = new Set();
                groupSources.set(groupId, sources);
            }
            return sources;
        };
        // 4. Process each group once, collecting sources bottom-up
        // We use a Set to track processed groups to ensure we don't process the same group multiple times
        // This is important because a group might be referenced multiple times in the tree
        const processedGroups = new Set();
        for (const group of groups) {
            // Skip if we've already processed this group
            // This prevents duplicate processing and ensures each group's sources are collected only once
            if (processedGroups.has(group.persistentId))
                continue;
            processedGroups.add(group.persistentId);
            // Get sources from direct components in this group
            // Components are the original source of source IDs, so we start by collecting from them
            // This is the foundation of our source propagation - without this, no sources would exist
            const groupSourcesSet = getOrCreateGroupSources(group.persistentId);
            for (const componentId of group.componentIds) {
                const sourceId = componentIdsToSourceId.get(componentId);
                const sourceName = componentIdsToSourceName.get(componentId);
                if (sourceId && sourceName) {
                    // Add source to this group's source set
                    // This represents the direct sources of this group from its components
                    groupSourcesSet.add({
                        id: sourceId,
                        name: sourceName,
                    });
                }
            }
            // Propagate sources up to all parent groups
            // We use a bottom-up approach because:
            // 1. It ensures we have all child sources before propagating to parents
            // 2. It naturally handles the hierarchical nature of the group structure
            // 3. It's more efficient than top-down as we only process each group once
            let currentGroup = group;
            while (currentGroup.parentGroupId) {
                const parentSources = getOrCreateGroupSources(currentGroup.parentGroupId);
                const currentSources = getOrCreateGroupSources(currentGroup.persistentId);
                // Add all current sources to parent's sources
                // This is where the propagation happens - each parent gets all sources from its children
                currentSources.forEach((source) => parentSources.add(source));
                // Move up to parent group
                // We continue until we reach a group with no parent (root group)
                const nextGroup = groupsById.get(currentGroup.parentGroupId);
                if (!nextGroup)
                    break;
                currentGroup = nextGroup;
            }
        }
        // 5. Finally, assign the collected sources to each group
        for (const [groupId, sources] of groupSources) {
            const group = groupsById.get(groupId);
            if (!group)
                continue;
            // Convert Set to Array and ensure unique sources by id
            // Even though we used Set during collection, we might have propagated the same source
            // multiple times up different branches of the tree. This final deduplication ensures
            // each source appears only once in the final array, regardless of how many paths
            // it took to reach this group
            const uniqueSources = Array.from(sources).reduce((acc, source) => {
                if (!acc.some((s) => s.id === source.id)) {
                    acc.push(source);
                }
                return acc;
            }, []);
            // Assign unique sources to group
            // This is the final step where we actually set the sources on the group object
            group.sources = uniqueSources;
            // Set first source as default for backwards compatibility
            // Some older code might expect sourceId and sourceName to be set directly
            // This ensures those properties are available even if the code doesn't use the sources array
            if (group.sources.length > 0 && group.sources[0]) {
                group.sourceId = group.sources[0].id;
                group.sourceName = group.sources[0].name;
            }
        }
    }
    recomputePaths(groups) {
        // Find roots, and compute the segments down from the roots
        for (const group of groups) {
            if (group.isRoot) {
                this.recomputePathsFromRoot(group, [], groups);
            }
        }
        // Drop first item because we don't want the core root category to be there
        for (const group of groups) {
            group.path.shift();
        }
    }
    recomputePathsFromRoot(root, segments, groups) {
        // Recursively go down the tree(s) and add segments to each
        const extendedPath = segments.concat(root.name);
        for (const groupId of root.subgroupIds) {
            const group = groups.find((g) => g.persistentId === groupId);
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            group.path = extendedPath.concat();
            // @ts-expect-error TS(2345): Argument of type 'DesignComponentGroup | undefined... Remove this comment to see the full error message
            this.recomputePathsFromRoot(group, extendedPath, groups);
        }
    }
    reorderGroupsByRoots(groups) {
        let sortedGroups = new Array();
        // Find the root groups, which will be initial sorting points
        const roots = groups.filter((g) => g.isRoot);
        for (const root of roots) {
            // For each group, traverse and add proper order
            sortedGroups = sortedGroups.concat(this.traverseSortGroup(root, groups));
        }
        return sortedGroups;
    }
    traverseSortGroup(group, groups) {
        let output = new Array();
        // Iterated group always first
        output.push(group);
        // Add sorted groups to the array
        const sortedGroupIds = group.subgroupIds.sort((g1, g2) => group.childrenIds.indexOf(g1) - group.childrenIds.indexOf(g2));
        for (const subgroupId of sortedGroupIds) {
            const subgroup = groups.find((g) => g.persistentId === subgroupId);
            // @ts-expect-error TS(2345): Argument of type 'DesignComponentGroup | undefined... Remove this comment to see the full error message
            output = output.concat(this.traverseSortGroup(subgroup, groups));
        }
        return output;
    }
    recomputeParents(groups) {
        // Find roots, and compute the references down the chain. Root groups don't have parents
        for (const group of groups) {
            if (group.isRoot) {
                this.recomputeParentsFromRoot(group, groups);
                group.parentGroupId = null;
            }
        }
    }
    recomputeParentsFromRoot(rootGroup, groups) {
        for (const subgroupId of rootGroup.subgroupIds) {
            const subgroup = groups.find((g) => g.persistentId === subgroupId);
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            subgroup.setParentGroupId(rootGroup.persistentId);
            // @ts-expect-error TS(2345): Argument of type 'DesignComponentGroup | undefined... Remove this comment to see the full error message
            this.recomputeParentsFromRoot(subgroup, groups);
        }
    }
}
